import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
    getPacketLossHistoryAPI,
    getRttHistoryAPI,
    HistoryRange,
    PacketLossHistoryData,
    RttHistoryData,
} from "./requests/request";
import PacketLossChart from "./PacketLossChart";
import RttChart from "./RttChart";
import RttBaselineLegend from "./RttBaselineLegend";
import {
    DEFAULT_RANGE,
    RANGE_OPTIONS,
    rangeFromLabel,
    rangeLabel,
    windowLabel,
} from "./historyChart";

type HistoryTab = "loss" | "rtt";

const TABS = [
    { value: "loss", label: "Packet loss" },
    { value: "rtt", label: "RTT" },
];

export interface TargetHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    uplinkLabel: string;
    source: string;
    operationId: string;
    targetIp: string;
    targetLabel: string;
}

/**
 * History of one IPSLA target: packets lost and RTT against its latency
 * baseline, as two tabs behind a single range selector.
 *
 * Each tab is fetched only when it is opened — the RTT response carries a
 * p95 over 7 days, by far the heaviest query of the view.
 */
export const TargetHistoryModal: React.FC<TargetHistoryModalProps> = ({
    isOpen,
    onClose,
    uplinkLabel,
    source,
    operationId,
    targetIp,
    targetLabel,
}) => {
    const api = useAxiosAuth0();
    const [tab, setTab] = useState<HistoryTab>("loss");
    const [range, setRange] = useState<HistoryRange>(DEFAULT_RANGE);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [loss, setLoss] = useState<PacketLossHistoryData | null>(null);
    const [rtt, setRtt] = useState<RttHistoryData | null>(null);

    // Switching range twice in a row can resolve out of order; only the
    // newest request is allowed to write.
    const requestId = useRef(0);

    const fetchActiveTab = useCallback(async () => {
        const currentRequest = ++requestId.current;
        setIsLoading(true);
        setHasError(false);
        try {
            if (tab === "loss") {
                const response = await getPacketLossHistoryAPI(
                    api,
                    source,
                    operationId,
                    range
                );
                if (currentRequest !== requestId.current) return;
                setLoss(response.data.data);
            } else {
                const response = await getRttHistoryAPI(
                    api,
                    source,
                    operationId,
                    range
                );
                if (currentRequest !== requestId.current) return;
                setRtt(response.data.data);
            }
        } catch {
            if (currentRequest !== requestId.current) return;
            setHasError(true);
        } finally {
            if (currentRequest === requestId.current) setIsLoading(false);
        }
    }, [api, source, operationId, range, tab]);

    useEffect(() => {
        if (!isOpen) return;
        fetchActiveTab();
    }, [isOpen, fetchActiveTab]);

    const active = tab === "loss" ? loss : rtt;
    const points = active?.points ?? [];

    const caption = useMemo(() => {
        if (!active) return "";
        const every = windowLabel(active.everySeconds);
        return tab === "loss"
            ? `Packets lost ${every}`
            : `Mean RTT ${every} (answered probes only)`;
    }, [active, tab]);

    /** Both ends come from one query, so they are known or unknown together. */
    const hasBaseline =
        rtt !== null &&
        (rtt.baseline.typicalMs !== null || rtt.baseline.unusualMs !== null);

    const onRttTab = tab === "rtt" && !isLoading && !hasError && rtt !== null;
    const showBaselineNotice = onRttTab && !hasBaseline;

    return (
        <GenericModal
            isOpen={isOpen}
            onClose={onClose}
            customWidth="820px"
            title={
                <div className="flex flex-col">
                    <span className="text-md font-semibold text-semantic-text-primary">
                        History — {targetIp}
                    </span>
                    <span className="text-xs text-semantic-text-secondary">
                        {uplinkLabel} · {targetLabel}
                    </span>
                </div>
            }
        >
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                    <GenericTabs
                        tabs={TABS}
                        activeTab={tab}
                        onTabChange={(value) => setTab(value as HistoryTab)}
                        size="xs"
                        fullWidth={false}
                        fitContent={true}
                        aria-label="History metric"
                    />
                    <GenericDropdown
                        items={RANGE_OPTIONS.map((opt) => opt.label)}
                        value={rangeLabel(range)}
                        onSelect={(label) => setRange(rangeFromLabel(label))}
                        widthClass="w-44"
                        loading={isLoading}
                    />
                </div>

                <span className="text-xs text-semantic-text-secondary">
                    {caption}
                    {showBaselineNotice &&
                        " · no reference lines yet (no answered probe in the last 7 days)"}
                </span>

                {onRttTab && hasBaseline && (
                    <RttBaselineLegend baseline={rtt.baseline} />
                )}

                {isLoading && (
                    <div className="flex h-[260px] items-center justify-center text-sm text-semantic-text-secondary">
                        Loading...
                    </div>
                )}

                {!isLoading && hasError && (
                    <div className="flex h-[260px] items-center justify-center text-sm text-semantic-text-secondary">
                        Error fetching history. Please try again later.
                    </div>
                )}

                {!isLoading && !hasError && points.length === 0 && (
                    <div className="flex h-[260px] items-center justify-center text-sm text-semantic-text-secondary">
                        No samples in this range.
                    </div>
                )}

                {!isLoading && !hasError && points.length > 0 && (
                    <div className="h-[260px] w-full">
                        {tab === "loss" ? (
                            <PacketLossChart
                                points={loss?.points ?? []}
                                range={range}
                            />
                        ) : (
                            <RttChart
                                points={rtt?.points ?? []}
                                range={range}
                                typicalMs={rtt?.baseline.typicalMs ?? null}
                                unusualMs={rtt?.baseline.unusualMs ?? null}
                            />
                        )}
                    </div>
                )}
            </div>
        </GenericModal>
    );
};

export default TargetHistoryModal;
